import type { AxiosError } from 'axios';
import type { MutableRefObject } from 'react';
import { Box, Button, Center, Flex, Group, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContractLine, Space } from '@/api/generated/models';
import type { SpaceNode } from '@/features/price-lists/spaceTreeUtils';
import type { DraftSummary, DraftValue } from '@/utils/useItemsDraft';
import { useListContractLines } from '@/api/generated/contracts/contracts';
import { useListUnits } from '@/api/generated/units/units';
import {
  getListLineAllocationsQueryKey,
  getListSpaceLinePlansByContractQueryKey,
  useListLineAllocations,
  useListSpaceLinePlansByContract,
  useSaveSpaceLinePlans
} from '@/api/generated/work-structure/work-structure';
import { IconFigmaInfo, IconFigmaXCircle } from '@/assets/icons';
import {
  DRAFT_ROW_BACKGROUND,
  DraftNumberInput,
  ItemsSaveBar,
  ItemsSaveStatus,
  NewItemBadge,
  StatusBanner,
  tableRowStyle,
  TextField,
  UndoRemoveButton
} from '@/components/common';
import { ContractSpaceLinePlanPickerModal } from '@/features/contracts/components/ContractSpaceLinePlanPickerModal';
import { buildTree } from '@/features/price-lists/spaceTreeUtils';
import { findUnitLabel } from '@/features/units/unitDisplay';
import { useItemsDraft } from '@/utils/useItemsDraft';

const spaceBreakdownErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

/** Nespremljene promjene razrade — stranica ih spaja sa stavkama u jedno pitanje pri izlasku. */
export interface SpaceDraftState {
  summary: DraftSummary;
  /** Koliko stavki ugovora nije potpuno raspoređeno (nakon spremanja, ako ima promjena). */
  notFullyAllocated: number;
}

export interface SpaceDraftHandle {
  save: () => Promise<boolean>;
  discard: () => void;
}

interface ContractSpaceBreakdownProps {
  contractId: string;
  contractLabel?: string;
  coveredSpaces: Space[];
  readOnly?: boolean;
  /** Pitanje Odbaci/Ostani/Spremi stranice — koristi se pri prelasku na drugi prostor. */
  guard: (action: () => void) => void;
  onDraftChange: (state: SpaceDraftState) => void;
  draftRef: MutableRefObject<SpaceDraftHandle | null>;
}

/** Redak razrade: planirana količina GRANE (ovaj prostor + podprostori) za jednu stavku ugovora. */
interface SpaceRow {
  key: string;
  line: ContractLine;
  tempId?: string;
  isNew: boolean;
  isRemoved: boolean;
  value: DraftValue;
  /** Spremljena količina grane (nova stavka: početna količina iz pickera). */
  savedValue: number;
  /** Koliko drže podprostori — grana ne može ispod toga. */
  floor: number;
  /** Koliko je za ovu granu još slobodno na ugovoru. */
  ceiling: number;
  canRemove: boolean;
  isChanged: boolean;
  error: string | null;
  /** Količina grane nakon spremanja (za Ras. / Ugov.). */
  effective: number;
}

const EMPTY_SUMMARY: DraftSummary = { changed: 0, added: 0, removed: 0, errors: 0, total: 0 };

/**
 * Razrada prostora — vlastite planirane količine prostora čekaju u nacrtu i spremaju se po
 * prostoru ("Spremi stavke", jedan zahtjev, jedna transakcija). Prelazak na drugi prostor s
 * nespremljenim promjenama pita Odbaci/Ostani/Spremi; prostor s promjenama ima točkicu u stablu.
 */
export const ContractSpaceBreakdown = ({
  contractId,
  contractLabel,
  coveredSpaces,
  readOnly = false,
  guard,
  onDraftChange,
  draftRef
}: ContractSpaceBreakdownProps) => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(
    coveredSpaces[0]?.id ?? null
  );
  const [pickerOpened, setPickerOpened] = useState(false);
  const draft = useItemsDraft<{ line: ContractLine; initialQty: number }>();
  const [justSaved, setJustSaved] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { reset: resetDraft } = draft;

  useEffect(() => {
    if (!selectedSpaceId && coveredSpaces[0]?.id) setSelectedSpaceId(coveredSpaces[0].id);
  }, [coveredSpaces, selectedSpaceId]);

  // Nacrt pripada jednom prostoru — prelazak (nakon pitanja) kreće od čistog stanja.
  useEffect(() => {
    resetDraft();
    setSaveError(null);
    setJustSaved(false);
  }, [selectedSpaceId, resetDraft]);

  const tree = useMemo(() => buildTree(coveredSpaces), [coveredSpaces]);

  // Djeca svakog prostora (unutar seta prostora obuhvaćenih ugovorom) — koristi
  // se za roll-up "grane" (ovaj prostor + svi njegovi podprostori) u tablici.
  const childrenMap = useMemo(() => {
    const map = new Map<string, string[]>();
    coveredSpaces.forEach((space) => {
      if (space.parentSpaceId && space.id) {
        map.set(space.parentSpaceId, [...(map.get(space.parentSpaceId) ?? []), space.id]);
      }
    });
    return map;
  }, [coveredSpaces]);

  const query = search.trim().toLowerCase();
  const matchesSearch = (node: SpaceNode): boolean => {
    if (!query) return true;
    if (node.name?.toLowerCase().includes(query) || node.code?.toLowerCase().includes(query))
      return true;
    return node.children.some(matchesSearch);
  };

  const { data: linesData } = useListContractLines(contractId);
  const lines: ContractLine[] = (linesData ?? []).filter((line) => !line.validTo);
  const { data: unitsData } = useListUnits();
  const { data: allocationsData } = useListLineAllocations(contractId);
  const allocations = allocationsData ?? [];
  const { data: spaceLinePlansData } = useListSpaceLinePlansByContract(contractId);
  const spaceLinePlans = spaceLinePlansData ?? [];
  const ownSpaceLinePlans = spaceLinePlans.filter((plan) => plan.spaceId === selectedSpaceId);

  // "Grana" = ovaj prostor + svi njegovi podprostori. Stavke djece se vide u
  // listi roditelja (zbrojene s njegovom vlastitom količinom) — korisnik na
  // roditelju uređuje samo UKUPNO za granu, razlika iznad zbroja djece postaje
  // roditeljeva vlastita planirana stavka.
  const branchSpaceIds = useMemo(() => {
    if (!selectedSpaceId) return new Set<string>();
    const collect = (id: string): string[] =>
      (childrenMap.get(id) ?? []).flatMap((childId) => [childId, ...collect(childId)]);
    return new Set([selectedSpaceId, ...collect(selectedSpaceId)]);
  }, [selectedSpaceId, childrenMap]);

  const ownQty = (lineId: string) =>
    ownSpaceLinePlans.find((plan) => plan.contractLineId === lineId)?.plannedQty ?? 0;
  const branchTotal = (lineId: string) =>
    spaceLinePlans
      .filter((plan) => plan.contractLineId === lineId && branchSpaceIds.has(plan.spaceId ?? ''))
      .reduce((sum, plan) => sum + (plan.plannedQty ?? 0), 0);
  const totalAllocated = (lineId: string) =>
    allocations.find((a) => a.contractLineId === lineId)?.allocatedQty ?? 0;
  const allocatedElsewhere = (lineId: string) => totalAllocated(lineId) - branchTotal(lineId);

  const nonNegativeError = t('itemsDraft.errorNonNegative');
  const rangeError = (value: DraftValue, floor: number, ceiling: number): string | null => {
    if (value === '') return nonNegativeError;
    if (value < floor) return t('contractSpaceBreakdown.fieldMin', { min: floor });
    if (value > ceiling) return t('contractSpaceBreakdown.fieldMax', { max: ceiling });
    return null;
  };

  // Prostor prikazuje samo stavke koje već imaju planiranu količinu na grani (ovaj prostor ili
  // neki od podprostora); nova stavka dolazi preko "+ Dodaj stavku" (ContractSpaceLinePlanPickerModal).
  const rows: SpaceRow[] = [
    ...lines
      .filter((line) => !!line.id && branchTotal(line.id) > 0)
      .map((line): SpaceRow => {
        const lineId = line.id as string;
        const branch = branchTotal(lineId);
        const floor = branch - ownQty(lineId);
        const ceiling = (line.quantity ?? 0) - allocatedElsewhere(lineId);
        const value = draft.valueOf(lineId, 'qty', branch);
        const isRemoved = draft.isRemoved(lineId);
        return {
          key: lineId,
          line,
          isNew: false,
          isRemoved,
          value,
          savedValue: branch,
          floor,
          ceiling,
          canRemove: ownQty(lineId) > 0,
          isChanged: !isRemoved && value !== branch,
          error: isRemoved ? null : rangeError(value, floor, ceiling),
          effective: isRemoved ? floor : value === '' ? branch : value
        };
      }),
    ...draft.added.map((item): SpaceRow => {
      const lineId = item.meta.line.id ?? '';
      const value = item.values.qty ?? 0;
      return {
        key: item.tempId,
        line: item.meta.line,
        tempId: item.tempId,
        isNew: true,
        isRemoved: false,
        value,
        savedValue: item.meta.initialQty,
        floor: 0,
        ceiling: (item.meta.line.quantity ?? 0) - totalAllocated(lineId),
        canRemove: true,
        isChanged: false,
        error: rangeError(value, 0, (item.meta.line.quantity ?? 0) - totalAllocated(lineId)),
        effective: value === '' ? 0 : value
      };
    })
  ];

  const allocatedAfterSave = (row: SpaceRow) =>
    row.isNew
      ? totalAllocated(row.line.id ?? '') + row.effective
      : allocatedElsewhere(row.line.id ?? '') + row.effective;

  const changedCount = rows.filter((row) => row.isChanged).length;
  const removedCount = rows.filter((row) => row.isRemoved).length;
  const summary: DraftSummary = selectedSpaceId
    ? {
        changed: changedCount,
        added: draft.added.length,
        removed: removedCount,
        errors: rows.filter((row) => row.error).length,
        total: changedCount + draft.added.length + removedCount
      }
    : EMPTY_SUMMARY;

  // Stavke ugovora koje nisu potpuno raspoređene — uz nespremljene promjene, stanje NAKON spremanja.
  const draftAllocation = new Map<string, number>();
  rows.forEach((row) => {
    if (row.line.id && (row.isNew || row.isChanged || row.isRemoved)) {
      draftAllocation.set(row.line.id, allocatedAfterSave(row));
    }
  });
  const notFullyAllocated = lines.filter(
    (line) =>
      (draftAllocation.get(line.id ?? '') ?? totalAllocated(line.id ?? '')) < (line.quantity ?? 0)
  ).length;

  useEffect(() => {
    onDraftChange({ summary, notFullyAllocated });
    // Ovisnosti su brojevi, ne objekt sažetka (on se gradi svaki render).
  }, [
    summary.changed,
    summary.added,
    summary.removed,
    summary.errors,
    summary.total,
    notFullyAllocated
  ]);
  useEffect(() => () => onDraftChange({ summary: EMPTY_SUMMARY, notFullyAllocated: 0 }), []);

  const saveMutation = useSaveSpaceLinePlans({
    mutation: { meta: { skipGlobalErrorToast: true } }
  });

  const save = async (): Promise<boolean> => {
    if (!selectedSpaceId || summary.errors > 0) return false;
    setSaveError(null);
    try {
      await saveMutation.mutateAsync({
        contractId,
        spaceId: selectedSpaceId,
        data: {
          lines: rows
            .filter((row) => row.isChanged || row.isRemoved || row.isNew)
            .map((row) => ({
              contractLineId: row.line.id as string,
              plannedQty: row.isRemoved ? 0 : row.effective - row.floor
            }))
        }
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getListSpaceLinePlansByContractQueryKey(contractId)
        }),
        queryClient.invalidateQueries({ queryKey: getListLineAllocationsQueryKey(contractId) })
      ]);
      draft.reset();
      setJustSaved(true);
      setSavedAt(
        new Date().toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
      );
      return true;
    } catch (error) {
      setSaveError(
        t('itemsDraft.saveFailed', {
          reason: spaceBreakdownErrorMessage(error, t('common.error'))
        })
      );
      return false;
    }
  };

  const discard = () => {
    draft.reset();
    setSaveError(null);
  };

  draftRef.current = { save, discard };

  const setRowValue = (row: SpaceRow, value: DraftValue) => {
    setJustSaved(false);
    if (row.tempId) draft.setAddedValue(row.tempId, 'qty', value);
    else if (row.line.id) draft.setValue(row.line.id, 'qty', value);
  };
  const removeRow = (row: SpaceRow) => {
    setJustSaved(false);
    if (row.tempId) draft.discardAdded(row.tempId);
    else if (row.line.id) draft.toggleRemoved(row.line.id);
  };

  const selectedSpace = coveredSpaces.find((s) => s.id === selectedSpaceId);
  const spaceScope = selectedSpace?.code || selectedSpace?.name || '';
  const anyRowHasAction = !readOnly && rows.some((row) => row.canRemove);
  const hasDraftState = (row: SpaceRow) =>
    row.isNew || row.isRemoved || row.isChanged || !!row.error;

  const renderAction = (row: SpaceRow) => {
    if (readOnly || !row.canRemove) return null;
    if (row.isRemoved) return <UndoRemoveButton onClick={() => removeRow(row)} />;
    return (
      <Box
        role="button"
        tabIndex={0}
        title={t('contractSpaceBreakdown.removeAction')}
        onClick={() => removeRow(row)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') removeRow(row);
        }}
        style={{ display: 'inline-block', cursor: 'pointer', color: 'var(--color-status-danger)' }}
      >
        <IconFigmaXCircle size={16} />
      </Box>
    );
  };

  const renderQuantity = (row: SpaceRow, mobile: boolean) =>
    readOnly ? (
      <Text fz={13} fw={600} ta="right" style={{ color: 'var(--color-text-primary)' }}>
        {row.savedValue}
      </Text>
    ) : (
      <DraftNumberInput
        width={mobile ? 96 : 84}
        height={mobile ? 40 : 32}
        value={row.value}
        savedValue={row.savedValue}
        isNew={row.isNew}
        error={row.error}
        disabled={row.isRemoved}
        ariaLabel={t('contractSpaceBreakdown.colPlannedHere')}
        onChange={(value) => setRowValue(row, value)}
      />
    );

  const allocatedLabel = (row: SpaceRow) => {
    const allocated = allocatedAfterSave(row);
    const full = allocated === (row.line.quantity ?? 0);
    return (
      <Text
        span
        fz={12.5}
        fw={full ? 400 : 700}
        style={{
          color: full ? 'var(--color-text-secondary)' : 'var(--color-accent-primary-active)'
        }}
      >
        {allocated} / {row.line.quantity}
      </Text>
    );
  };

  const renderSpaceNode = (node: SpaceNode, depth: number): React.ReactNode => {
    if (!matchesSearch(node)) return null;
    const isSelected = node.id === selectedSpaceId;
    const hasUnsaved = isSelected && summary.total > 0;
    const select = () => {
      if (!node.id || isSelected) return;
      const spaceId = node.id;
      guard(() => setSelectedSpaceId(spaceId));
    };
    return (
      <Box key={node.id}>
        <Group
          role="button"
          tabIndex={0}
          onClick={select}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') select();
          }}
          gap={8}
          wrap="nowrap"
          justify="space-between"
          style={{
            padding: '10px 12px',
            marginLeft: depth * 16,
            marginBottom: 4,
            borderRadius: 8,
            cursor: 'pointer',
            backgroundColor: isSelected ? 'var(--color-accent-primary-soft)' : 'transparent'
          }}
        >
          <Text
            fz={13}
            fw={isSelected ? 700 : 500}
            style={{
              color: isSelected
                ? 'var(--color-accent-primary-active)'
                : 'var(--color-text-secondary)'
            }}
            truncate
          >
            {node.name}
          </Text>
          {hasUnsaved && (
            <Box
              title={t('itemsDraft.statusDirty')}
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: 'var(--color-accent-primary-active)'
              }}
            />
          )}
        </Group>
        {node.children.map((child) => renderSpaceNode(child, depth + 1))}
      </Box>
    );
  };

  return (
    <Flex
      direction={{ base: 'column', sm: 'row' }}
      align={{ base: 'stretch', sm: 'flex-start' }}
      gap={16}
    >
      <Stack
        gap={12}
        p={16}
        w={{ base: '100%', sm: 280 }}
        style={{
          borderRadius: 12,
          border: '1px solid var(--color-border-soft)',
          backgroundColor: 'var(--color-bg-surface)'
        }}
      >
        <Text fz={15} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('contractSpaceBreakdown.title')}
        </Text>
        <TextField
          hideLabel
          placeholder={t('contractSpaceBreakdown.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <Stack gap={0}>
          {tree.map((node) => renderSpaceNode(node, 0))}
          {tree.length === 0 && (
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractSpaceBreakdown.noCoveredSpaces')}
            </Text>
          )}
        </Stack>
      </Stack>

      <Stack
        gap={16}
        style={{
          flex: 1,
          minWidth: 0,
          borderRadius: 12,
          border: '1px solid var(--color-border-soft)',
          backgroundColor: 'var(--color-bg-surface)',
          overflow: 'hidden'
        }}
      >
        {!selectedSpace ? (
          <Center py={40}>
            <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
              {t('contractSpaceBreakdown.noCoveredSpaces')}
            </Text>
          </Center>
        ) : (
          <>
            <Stack gap={8} px={20} pt={20}>
              <Stack gap={4}>
                <Text fz={16} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                  {selectedSpace.name}
                </Text>
                <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                  {t('contractSpaceBreakdown.subtitle')}
                </Text>
              </Stack>

              <Group justify="space-between" align="center" gap={12}>
                <Group gap={8}>
                  <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                    {t('contractSpaceBreakdown.itemsLabel')}
                  </Text>
                  <Box
                    px={12}
                    py={4}
                    style={{ borderRadius: 999, backgroundColor: 'var(--color-bg-surface-2)' }}
                  >
                    <Text fz={11} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
                      {t('contractSpaceBreakdown.itemsBadge', { count: rows.length })}
                    </Text>
                  </Box>
                </Group>
                {!readOnly && (
                  <Group gap={12} wrap="nowrap">
                    <ItemsSaveStatus summary={summary} justSaved={justSaved} savedAt={savedAt} />
                    <Button size="xs" variant="outline" onClick={() => setPickerOpened(true)}>
                      {t('contractSpaceBreakdown.addItem')}
                    </Button>
                  </Group>
                )}
              </Group>
            </Stack>

            {saveError && (
              <Box px={20}>
                <StatusBanner
                  tone="danger"
                  message={saveError}
                  onDismiss={() => setSaveError(null)}
                />
              </Box>
            )}

            {rows.length === 0 && (
              <Text fz={13} px={20} style={{ color: 'var(--color-text-muted)' }}>
                {t('contractSpaceBreakdown.noPlannedItems')}
              </Text>
            )}

            {rows.length > 0 && (
              <Box visibleFrom="sm" style={{ overflowX: 'auto' }}>
                <Box style={{ minWidth: 640 }}>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '10px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderTop: '1px solid var(--color-border-soft)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    {[
                      [t('contractDetail.colCode'), 70, 'left'],
                      [t('contractDetail.colDescription'), undefined, 'left'],
                      [t('contractSpaceBreakdown.colAllocatedContracted'), 90, 'right'],
                      [t('contractDetail.colUnit'), 60, 'center'],
                      [t('contractSpaceBreakdown.colPlannedHere'), 110, 'right'],
                      ...(anyRowHasAction
                        ? [[t('contractSpaceBreakdown.colActions'), 60, 'right']]
                        : [])
                    ].map(([label, width, align]) => (
                      <Text
                        key={String(label)}
                        fz={9.5}
                        fw={700}
                        tt="uppercase"
                        ta={align as 'left' | 'right' | 'center'}
                        style={{
                          width: width as number | undefined,
                          flex: width ? undefined : 1,
                          color: 'var(--color-text-muted)'
                        }}
                      >
                        {label}
                      </Text>
                    ))}
                  </div>

                  {rows.map((row, index) => {
                    const muted = row.isRemoved ? 'var(--color-text-muted)' : undefined;
                    return (
                      <div
                        key={row.key}
                        style={{
                          ...tableRowStyle,
                          padding: '12px 20px',
                          borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)',
                          backgroundColor: hasDraftState(row) ? DRAFT_ROW_BACKGROUND : undefined
                        }}
                      >
                        <Text
                          fz={13}
                          fw={600}
                          style={{ width: 70, color: muted ?? 'var(--color-text-primary)' }}
                        >
                          {row.line.itemCode}
                        </Text>
                        <Group
                          gap={8}
                          wrap="nowrap"
                          justify="space-between"
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <Text
                            fz={12.5}
                            truncate
                            style={{
                              minWidth: 0,
                              color: muted ?? 'var(--color-text-secondary)',
                              textDecoration: row.isRemoved ? 'line-through' : undefined
                            }}
                          >
                            {row.line.description}
                          </Text>
                          {row.isNew && <NewItemBadge />}
                        </Group>
                        <Box style={{ width: 90, textAlign: 'right' }}>{allocatedLabel(row)}</Box>
                        <Text
                          fz={12.5}
                          ta="center"
                          style={{ width: 60, color: muted ?? 'var(--color-text-secondary)' }}
                        >
                          {findUnitLabel(unitsData, row.line.unitId, i18n.language)}
                        </Text>
                        <Box style={{ width: 110, display: 'flex', justifyContent: 'flex-end' }}>
                          {renderQuantity(row, false)}
                        </Box>
                        {anyRowHasAction && (
                          <Box style={{ width: 60, textAlign: 'right' }}>{renderAction(row)}</Box>
                        )}
                      </div>
                    );
                  })}
                </Box>
              </Box>
            )}

            {rows.length > 0 && (
              <Stack hiddenFrom="sm" gap={12} px={16}>
                {rows.map((row) => (
                  <Stack
                    key={row.key}
                    gap={8}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-soft)',
                      backgroundColor: hasDraftState(row)
                        ? DRAFT_ROW_BACKGROUND
                        : 'var(--color-bg-surface)'
                    }}
                  >
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap={8} wrap="nowrap">
                        <Text fz={12.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                          {t('contractDetail.cardCode', { code: row.line.itemCode ?? '—' })}
                        </Text>
                        {row.isNew && <NewItemBadge />}
                      </Group>
                      {renderAction(row)}
                    </Group>
                    <Text
                      fz={13.5}
                      style={{
                        color: row.isRemoved
                          ? 'var(--color-text-muted)'
                          : 'var(--color-text-secondary)',
                        textDecoration: row.isRemoved ? 'line-through' : undefined
                      }}
                    >
                      {row.line.description}
                    </Text>
                    <Group gap={12} wrap="wrap" style={{ rowGap: 4 }}>
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('contractSpaceBreakdown.cardAllocated')} {allocatedLabel(row)}
                      </Text>
                      <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                        {t('contractSpaceBreakdown.cardUnit')}{' '}
                        <Text span fz={11} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                          {findUnitLabel(unitsData, row.line.unitId, i18n.language)}
                        </Text>
                      </Text>
                    </Group>
                    <Group justify="space-between" wrap="nowrap" align="flex-start">
                      <Text
                        fz={13}
                        pt={readOnly ? 0 : 10}
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        {t('contractSpaceBreakdown.colPlannedHere')}
                      </Text>
                      {renderQuantity(row, true)}
                    </Group>
                  </Stack>
                ))}
              </Stack>
            )}

            <Box px={20} pb={readOnly || summary.total === 0 ? 20 : 0}>
              <StatusBanner
                tone="info"
                icon={<IconFigmaInfo size={16} />}
                message={t('contractSpaceBreakdown.infoText')}
              />
            </Box>

            {!readOnly && (
              <Box visibleFrom="sm">
                <ItemsSaveBar
                  summary={summary}
                  isSaving={saveMutation.isPending}
                  onCancel={discard}
                  onSave={save}
                  scope={spaceScope}
                />
              </Box>
            )}
          </>
        )}
      </Stack>

      {!readOnly && (
        <Box hiddenFrom="sm">
          <ItemsSaveBar
            variant="mobile"
            summary={summary}
            isSaving={saveMutation.isPending}
            onCancel={discard}
            onSave={save}
            scope={spaceScope}
          />
        </Box>
      )}

      {selectedSpaceId && !readOnly && (
        <ContractSpaceLinePlanPickerModal
          opened={pickerOpened}
          onClose={() => setPickerOpened(false)}
          contractId={contractId}
          contractLabel={contractLabel}
          existingContractLineIds={[
            ...ownSpaceLinePlans.map((plan) => plan.contractLineId),
            ...draft.added.map((item) => item.meta.line.id)
          ].filter((id): id is string => !!id)}
          onAdd={(items) => {
            setJustSaved(false);
            draft.addItems(
              items.map(({ line, plannedQty }) => ({
                meta: { line, initialQty: plannedQty },
                values: { qty: plannedQty }
              }))
            );
          }}
        />
      )}
    </Flex>
  );
};
